import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'

import { PaymentRow, LedgerPeriod } from '../../../data/interfaces/lease-ledger.interface'

/** Por debajo de esto un saldo es residuo (espejo de DELINQUENCY_FLOOR en payments/utils.ts, F32). */
const RESIDUE_FLOOR = 10

/** Marco del SVG de la línea de tiempo. El `viewBox` se estira a lo ancho del panel (≈400 px), así
 *  que a tamaño real el texto queda casi 1:1 en vez de comprimido. */
const CHART = { left: 22, right: 378, minSpanDays: 16, padDays: 4 }

//* Coordenadas ya resueltas de la línea de tiempo: el template solo las pinta.
export interface MoraChart {
  due: { x: number; label: string }
  /** Franja roja del vencimiento hasta el pago (o hasta hoy si sigue abierta). */
  overdueBand: { x: number; width: number } | null
  /** Franja ámbar de lo que tardamos en registrar cada pago. */
  lagBands: { x: number; width: number }[]
  paids: { x: number; label: string }[]
  registers: { x: number; label: string }[]
  today: { x: number; label: string } | null
}

@Component({
  selector: 'app-period-detail',
  templateUrl: './period-detail.component.html',
  styleUrls: ['./period-detail.component.scss'],
})
export class PeriodDetailComponent implements OnChanges {
  @Input() period!: LedgerPeriod
  /** Solo la primera letra elegible ofrece «Aplicar» (A1 del intake): no se puede elegir letra. */
  @Input() isNextToCollect = false
  @Input() unapplied: PaymentRow[] = []
  @Output() apply = new EventEmitter<void>()
  @Output() openPayment = new EventEmitter<string>()
  //* Condonar desde la letra (F28): el contenedor abre `edit-amounts-modal` con `presetWaivePeriod`.
  @Output() waive = new EventEmitter<number>()

  readonly conceptLabels: Record<string, string> = { interest: 'Interés', moratoriums: 'Mora', accessories: 'Accesorios', capital: 'Capital', inputIva: 'IVA' }

  //* Mismo arreglo en cada ciclo de detección de cambios: `conceptKeys()` se llama desde un `*ngFor`
  //* y devolver uno nuevo cada vez repintaría el reparto de todas las tarjetas.
  private readonly conceptOrder: string[] = ['interest', 'moratoriums', 'accessories', 'capital', 'inputIva']
  readonly eventLabels: Record<string, string> = {
    APPLIED: 'Pago aplicado',
    CANCELLED: 'Aplicación cancelada',
    REGISTERED: 'Pago registrado',
    INVOICED: 'Facturada',
    REINVOICED: 'Refacturada',
    RECONCILED: 'Factura conciliada',
    EDITED: 'Saldos editados',
    WAIVED: 'Mora condonada',
  }

  //* Los dos se construyen con objetos nuevos: como getters, cada ciclo de detección de cambios
  //* daría un arreglo distinto y `*ngFor` reharía los chips y el SVG entero.
  driftChips: { label: string; tone: 'danger' | 'warn' }[] = []
  moraChart!: MoraChart

  ngOnChanges(): void {
    this.driftChips = this.buildDriftChips()
    this.moraChart = this.buildChart()
  }

  get liveApplications() {
    return this.period.applications.filter((a) => a.status !== 'CANCELLED')
  }

  /** Etiqueta de estado del bloque Mora, cuatro estados: «en pausa» si hay mora guardada y el interruptor
   *  está apagado en PAUSE (F27) · «devengando» si hay saldo que devenga y días corridos · «sin mora» si la
   *  letra debe pero aún no vence · «cerrada» en el resto. */
  get moraState(): { label: string; severity: 'danger' | 'success' | 'info' | 'warning' } {
    const mora = this.period.mora
    if (mora.paused && mora.accrued > 0) return { label: 'en pausa', severity: 'warning' }
    if (mora.balance > 0 && mora.days > 0) return { label: 'devengando', severity: 'danger' }
    if (this.period.pending > 0 && mora.days === 0) return { label: 'sin mora', severity: 'info' }
    return { label: 'cerrada', severity: 'success' }
  }

  /**
   * Solo el nombre de pila de quien aplicó, como el prototipo aprobado.
   *
   * La columna mide ~170 px y «Arnold Admin · 10/09/2026» se pasa por dos: con el apellido, lo que
   * se recortaba era la FECHA. El nombre completo queda en el `title` y en «Movimientos de la letra».
   */
  firstName(nombre: string | null | undefined): string {
    const limpio = (nombre || '').trim()
    return limpio ? limpio.split(/\s+/)[0] : '—'
  }

  /** Desfase pagó → registrado en días (F5): se muestra, no se cobra. 0 si no hay desfase o falta una fecha. */
  daysBetween(from: string | null | undefined, to: string | null | undefined): number {
    if (!from || !to) return 0
    return Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000))
  }

  get isResidue(): boolean {
    return this.period.pending > 0 && this.period.pending <= RESIDUE_FLOOR
  }

  /**
   * Los cinco conceptos del reparto, en orden, aunque el pago no haya tocado alguno.
   *
   * Antes solo salían los que traían monto, así que una tarjeta que decía «Interés $0.03» dejaba al
   * lector sin saber si a capital no se aplicó nada o si el dato no venía. El cero explícito es la
   * respuesta: se lee de un vistazo a qué NO se aplicó, que es justo lo que se revisa. De paso todas
   * las tarjetas miden lo mismo, porque el reparto ya no cambia de tamaño con el pago.
   *
   * Una clave que no esté en el orden conocido se agrega al final en vez de desaparecer, y en ese
   * caso —solo en ese— se devuelve un arreglo nuevo.
   */
  conceptKeys(byConcept: Record<string, number>): string[] {
    const extra = Object.keys(byConcept || {}).filter((k) => !this.conceptOrder.includes(k))
    return extra.length ? [...this.conceptOrder, ...extra] : this.conceptOrder
  }

  /** Color del punto de cada movimiento: qué pasó se distingue antes de leer la línea. */
  eventTone(type: string): string {
    if (type === 'CANCELLED') return 'red'
    if (type === 'REGISTERED') return 'gray'
    if (type === 'EDITED' || type === 'WAIVED') return 'warn'
    if (type === 'INVOICED' || type === 'REINVOICED' || type === 'RECONCILED') return 'inv'
    return 'app'
  }

  /**
   * Renta anticipada que se cobró con el pago inicial: lo aplicado no tiene aplicaciones detrás porque el pago
   * inicial no se reparte por letra. Ahí el desfase contra el libro es el diseño, no un dato roto, y los chips
   * «aplicado ≠ libro» asustaban en las 13 anticipadas del cuadre con Contabilidad (C5).
   */
  get coveredByDownPayment(): boolean {
    const p = this.period
    return !!p && p.status === 'ANTICIPATED' && !this.liveApplications.length && (p.applied?.total ?? 0) > 0
  }

  /** Chips de desfase (F5): se muestran, no se corrigen. */
  private buildDriftChips(): { label: string; tone: 'danger' | 'warn' }[] {
    const d = this.period?.drift
    if (!d || this.coveredByDownPayment) return []
    const chips: { label: string; tone: 'danger' | 'warn' }[] = []
    if (d.paidWithBalance) chips.push({ label: 'marcada pagada con saldo', tone: 'danger' })
    if (Math.abs(d.appliedPaymentVsLedger) > 0.01) chips.push({ label: `aplicado ≠ libro (${this.signedMoney(d.appliedPaymentVsLedger)})`, tone: 'warn' })
    for (const k of Object.keys(d.appliedAmountsVsLedger || {})) {
      const v = d.appliedAmountsVsLedger[k]
      if (Math.abs(v) > 0.01) chips.push({ label: `${this.conceptLabels[k] || k} ≠ libro (${this.signedMoney(v)})`, tone: 'warn' })
    }
    return chips
  }

  //* Con signo y en pesos, como el resto de la fila: «+53013.44» se leía como un número suelto.
  private signedMoney(v: number): string {
    const monto = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Math.abs(v))
    return `${v > 0 ? '+' : '−'}${monto}`
  }

  /**
   * Resuelve la línea de tiempo de la letra a coordenadas del SVG.
   *
   * El vencimiento y la fecha en que el cliente pagó son fechas de CALENDARIO guardadas a medianoche
   * UTC: sus etiquetas se formatean en UTC porque en CDMX (UTC−6) se leerían un día antes. El
   * registro y «hoy» sí son instantes reales y van en hora local, igual que en la tabla.
   */
  private buildChart(): MoraChart {
    const vivas = this.period?.applications?.filter((a) => a.status !== 'CANCELLED') ?? []
    const abierta = (this.period?.pending ?? 0) > 0
    const hoy = Date.now()
    const vence = new Date(this.period.date).getTime()
    const dia = 86400000

    const fin = abierta ? hoy : Math.max(vence, ...vivas.map((a) => Math.max(this.ms(a.paidOn), this.ms(a.registeredOn))))
    const inicio = Math.min(vence, ...vivas.map((a) => this.ms(a.paidOn))) - CHART.padDays * dia
    const span = Math.max(fin - inicio, CHART.minSpanDays * dia) + CHART.padDays * dia
    const x = (t: number) => CHART.left + ((t - inicio) / span) * (CHART.right - CHART.left)

    const ultimoPago = vivas.length ? Math.max(...vivas.map((a) => this.ms(a.paidOn))) : vence
    const corte = x(abierta ? hoy : ultimoPago)
    const xVence = x(vence)

    return {
      due: { x: xVence, label: `Vence ${this.fecha(this.period.date, true)}` },
      overdueBand: corte > xVence ? { x: xVence, width: corte - xVence } : null,
      lagBands: vivas.map((a) => ({ x: x(this.ms(a.paidOn)), width: x(this.ms(a.registeredOn)) - x(this.ms(a.paidOn)) })).filter((b) => b.width > 1),
      paids: vivas.map((a) => ({ x: x(this.ms(a.paidOn)), label: `Pagó ${this.fecha(a.paidOn, true)}` })),
      registers: vivas
        .filter((a) => this.daysBetween(a.paidOn, a.registeredOn) > 0)
        .map((a) => ({ x: x(this.ms(a.registeredOn)), label: `registrado +${this.daysBetween(a.paidOn, a.registeredOn)} d` })),
      today: abierta ? { x: x(hoy), label: `Hoy · ${Math.max(0, Math.floor((hoy - vence) / dia))} d` } : null,
    }
  }

  private ms(fecha: string): number {
    return new Date(fecha).getTime()
  }

  private fecha(iso: string, utc: boolean): string {
    const d = new Date(iso)
    const dd = utc ? d.getUTCDate() : d.getDate()
    const mm = (utc ? d.getUTCMonth() : d.getMonth()) + 1
    return `${String(dd).padStart(2, '0')}/${String(mm).padStart(2, '0')}`
  }
}
